import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Link as MuiLink,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  supportAgentApi,
  ActivityListItem,
  SupportAgentTicketRecord,
  SupportAgentTicketMode,
  SupportAgentTicketCategory,
  DraftVerdict,
  ActivityThreadComment,
  ActivityThreadResult,
  PostingImmediatePostResult,
} from './support-agent-api';
import { SafeMarkdown } from './safe-markdown';

const zendeskTicketUrl = (id: number) => `https://pomsafe.zendesk.com/agent/tickets/${id}`;

// Shown when this record's `recovered` flag is set — the note was replayed
// by the worker's crash-recovery path, not a normal drafting run, so no
// analysis (topic/confidence/urgencyReason/citations) exists for it.
const RECOVERED_TOOLTIP =
  'This note was re-attached by crash recovery — no analysis data exists for this record.';

const RECOVERED_ZERO_CITATIONS_COPY =
  'Recovered record — this note was restored by crash recovery; no retrieval/analysis data was captured.';

const RecoveredChip: React.FC = () => (
  <Tooltip title={RECOVERED_TOOLTIP}>
    <Chip size="small" color="warning" variant="outlined" label="Recovered" />
  </Tooltip>
);

const VerdictChip: React.FC<{ verdict: DraftVerdict }> = ({ verdict }) => {
  if (verdict === 'up') {
    return <Chip size="small" color="success" icon={<ThumbUpIcon fontSize="small" />} label="Up" />;
  }
  if (verdict === 'down') {
    return <Chip size="small" color="error" icon={<ThumbDownIcon fontSize="small" />} label="Down" />;
  }
  return <Chip size="small" variant="outlined" label="No verdict" />;
};

// ── Mode chip (Shadow / Observed / Live) ──────────────────────────────────
// Replaces the old single-purpose "Observed" grid column now that a record
// carries `mode` — see SupportAgentTicketMode. Records predating `mode`
// only ever carried the boolean `observed` flag, and everything that wasn't
// observe-only was, by definition, posted to the ticket (shadow mode didn't
// exist yet) — so a legacy record resolves to 'observe' or 'live', never
// 'shadow'.
type ResolvedMode = SupportAgentTicketMode;

function resolveMode(item: { mode?: SupportAgentTicketMode; observed?: boolean }): ResolvedMode {
  if (item.mode) return item.mode;
  return item.observed ? 'observe' : 'live';
}

const MODE_CHIP_CONFIG: Record<
  ResolvedMode,
  { label: string; color: 'warning' | 'info' | 'success'; tooltip: string }
> = {
  shadow: {
    label: 'Shadow',
    color: 'warning',
    tooltip: 'Draft generated for review, NOT sent to Zendesk.',
  },
  observe: {
    label: 'Observed',
    color: 'info',
    tooltip: 'The bot recorded this ticket without drafting (observe mode).',
  },
  live: {
    label: 'Live',
    color: 'success',
    tooltip: 'The draft was posted to the ticket as a private note.',
  },
};

const ModeChip: React.FC<{ mode: ResolvedMode }> = ({ mode }) => {
  const cfg = MODE_CHIP_CONFIG[mode];
  return (
    <Tooltip title={cfg.tooltip}>
      <Chip size="small" color={cfg.color} variant="outlined" label={cfg.label} />
    </Tooltip>
  );
};

// ── Skipped chip (sales/vendor + spam pre-draft triage, and the filtered-
//    senders list) ─────────────────────────────────────────────────────────
// A skipped record never reaches drafting at all. Rendered in place of the
// Mode chip (grid) / as its own compact timeline line (drawer) — a skipped
// record has no meaningful mode to show instead. Uses `default` (grey) so it
// reads as distinct from Shadow (warning/orange), Observed (info/blue), Live
// (success/green), and Recovered (warning outlined) at a glance.
//
// 'ignored_sender' (Carlos, 8/18) is rendered with its OWN label/tooltip
// below, deliberately NOT folded into this sales/spam label map — a
// filtered sender is an admin-managed allow/deny decision (Settings tab),
// not a spam classification, and must never read to an admin as "the bot
// thinks this customer is spam."
const SKIP_CATEGORY_LABEL: Partial<Record<SupportAgentTicketCategory, string>> = {
  sales_or_vendor: 'sales',
  spam_or_automated: 'spam',
};

const SKIPPED_TOOLTIP =
  "The bot classified this as sales/vendor outreach (or automated spam) and deliberately didn't draft. The ticket itself is untouched in Zendesk.";

const FILTERED_SENDER_TOOLTIP =
  'This sender is on the filter list — the bot never drafts or posts for them; humans can still handle the ticket normally.';

const SkippedChip: React.FC<{ category?: SupportAgentTicketCategory }> = ({ category }) => {
  if (category === 'ignored_sender') {
    return (
      <Tooltip title={FILTERED_SENDER_TOOLTIP}>
        <Chip size="small" color="default" variant="outlined" label="Filtered · sender" />
      </Tooltip>
    );
  }
  const suffix = category ? SKIP_CATEGORY_LABEL[category] : undefined;
  return (
    <Tooltip title={SKIPPED_TOOLTIP}>
      <Chip size="small" color="default" variant="outlined" label={suffix ? `Skipped · ${suffix}` : 'Skipped'} />
    </Tooltip>
  );
};

// ── Posting chip (per-ticket posting enrollment, Carlos 8/17) ─────────────
// Shown on a grid row (via ActivityListItem.postingEnabled) AND next to the
// drawer's posting Switch — same component in both places, distinct
// ('secondary'/purple) from Mode's warning/info/success so an enrolled
// ticket reads as its own thing at a glance, not just "currently Live".
const POSTING_CHIP_TOOLTIP =
  'This ticket is enrolled — every future draft posts to Zendesk as a private note, regardless of the global agent mode.';

const PostingChip: React.FC = () => (
  <Tooltip title={POSTING_CHIP_TOOLTIP}>
    <Chip size="small" color="secondary" label="Posting" />
  </Tooltip>
);

// Snackbar copy for the outcome of enabling posting — see
// PostingImmediatePostResult. `undefined` (e.g. a disable call, which never
// carries `immediatePost`) falls back to a generic confirmation.
function immediatePostMessage(immediatePost?: PostingImmediatePostResult): string {
  if (!immediatePost) return 'Posting enabled';
  if (immediatePost.posted) return 'Draft posted as internal note';
  switch (immediatePost.reason) {
    case 'no_draft':
      return 'Enabled — no draft to post yet';
    case 'already_posted':
      return 'Enabled — draft was already on the ticket';
    case 'error':
      return 'Enabled — immediate post failed, next draft will post';
    case 'ticket_closed':
      return 'Enabled — this ticket is closed in Zendesk, so nothing can be posted to it.';
    default:
      return 'Posting enabled';
  }
}

// Shown inline in the Conversation section when GET .../thread fails — the
// rest of the drawer (verdict UI, timeline, etc.) does not depend on the
// thread and must keep working.
const THREAD_UNAVAILABLE_COPY = 'Conversation unavailable right now';

type ThreadCommentRole = 'customer' | 'agent' | 'internal' | 'bot';

interface ClassifiedThreadComment extends ActivityThreadComment {
  role: ThreadCommentRole;
}

/**
 * `ActivityThreadComment` has no author-role field — only `isBot` and
 * `public` (see pom-2-api thread.service.ts `ThreadComment`). Bot comments
 * are unambiguous (the AI's own private-note drafts). For the rest: a
 * Zendesk ticket is always opened by its requester and comments come back
 * oldest-first, so the first public, non-bot comment's author is "the
 * customer" for the whole thread — any other author posting publicly is an
 * agent. Non-public, non-bot comments are internal notes.
 */
function classifyThreadComments(comments: ActivityThreadComment[]): ClassifiedThreadComment[] {
  const customerAuthorId = comments.find((c) => c.public && !c.isBot)?.authorId;
  return comments.map((c) => {
    if (c.isBot) return { ...c, role: 'bot' };
    if (!c.public) return { ...c, role: 'internal' };
    return { ...c, role: c.authorId === customerAuthorId ? 'customer' : 'agent' };
  });
}

const THREAD_ROLE_STYLES: Record<ThreadCommentRole, { align: 'flex-start' | 'flex-end' | 'stretch'; tint: string }> = {
  customer: { align: 'flex-start', tint: 'transparent' },
  agent: { align: 'flex-end', tint: alpha('#1976d2', 0.08) },
  internal: { align: 'stretch', tint: alpha('#ed6c02', 0.12) },
  bot: { align: 'stretch', tint: alpha('#9c27b0', 0.1) },
};

const ThreadCommentBubble: React.FC<{ comment: ClassifiedThreadComment }> = ({ comment }) => {
  const style = THREAD_ROLE_STYLES[comment.role];
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: style.tint,
        border: '1px solid',
        borderColor: comment.role === 'customer' ? 'divider' : style.tint,
        alignSelf: style.align,
        maxWidth: comment.role === 'customer' || comment.role === 'agent' ? '85%' : '100%',
        width: comment.role === 'internal' || comment.role === 'bot' ? '100%' : undefined,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {comment.authorName}
        </Typography>
        {comment.role === 'bot' && <Chip size="small" color="secondary" label="AI draft" />}
        {comment.role === 'internal' && <Chip size="small" variant="outlined" color="warning" label="Internal note" />}
        <Typography variant="caption" color="text.secondary">
          {new Date(comment.createdAt).toLocaleString()}
        </Typography>
      </Stack>
      {comment.role === 'bot' ? (
        // Bot notes contain markdown — same renderer as the draft cards below.
        <SafeMarkdown source={comment.body} />
      ) : (
        // Everyone else's body is rendered as plain text with whitespace
        // preserved — NOT markdown, so a customer's message can't inject
        // formatting into the drawer.
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {comment.body}
        </Typography>
      )}
    </Box>
  );
};

// ── Bot activity timeline (per-record cards) ──────────────────────────────

interface TimelineRecordCardProps {
  record: SupportAgentTicketRecord;
  docTitles: Record<string, string>;
  submitting: 'up' | 'down' | null;
  onSubmitVerdict: (verdict: 'up' | 'down', note: string) => void;
}

/**
 * One card per append-only record in the drawer's "Bot activity" timeline
 * (newest first). Observe-only records never reach this component — see
 * the compact-line branch in the parent render below — so every card here
 * is draft-bearing (shadow, live, or a legacy pre-`mode` drafted record).
 * Verdict submission always targets THIS card's `record.recordId`, never
 * the ticket's latest record, so clicking an older card can never stamp the
 * wrong draft.
 */
const TimelineRecordCard: React.FC<TimelineRecordCardProps> = ({
  record,
  docTitles,
  submitting,
  onSubmitVerdict,
}) => {
  const [note, setNote] = useState('');
  const mode = resolveMode(record);

  return (
    <Box
      data-testid={`timeline-card-${record.recordId}`}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {new Date(record.createdAt).toLocaleString()}
        </Typography>
        <ModeChip mode={mode} />
        {record.recovered && <RecoveredChip />}
        {record.topic && <Chip size="small" label={record.topic} />}
        {typeof record.confidence === 'number' && (
          <Chip size="small" variant="outlined" label={`Confidence ${Math.round(record.confidence * 100)}%`} />
        )}
        {record.urgent && <Chip size="small" color="error" label="Urgent" />}
      </Stack>

      {record.urgent && record.urgencyReason && (
        <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
          {record.urgencyReason}
        </Typography>
      )}

      {mode === 'shadow' && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Shadow draft — generated for review, not sent
        </Alert>
      )}

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Draft
        </Typography>
        {record.draftReply ? (
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <SafeMarkdown source={record.draftReply} />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No draft recorded.
          </Typography>
        )}
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Sources cited
        </Typography>
        {(record.citedDocIds ?? []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {record.recovered
              ? RECOVERED_ZERO_CITATIONS_COPY
              : 'No doc coverage — the bot said so on this draft.'}
          </Typography>
        ) : (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {(record.citedDocIds ?? []).map((id) => (
              <Chip key={id} size="small" label={docTitles[id] ?? id} />
            ))}
          </Stack>
        )}
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      <TextField
        size="small"
        fullWidth
        placeholder="Optional note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        sx={{ mb: 1 }}
      />
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant={record.verdict === 'up' ? 'contained' : 'outlined'}
          color="success"
          startIcon={<ThumbUpIcon />}
          disabled={submitting !== null}
          onClick={() => onSubmitVerdict('up', note)}
        >
          Good draft
        </Button>
        <Button
          variant={record.verdict === 'down' ? 'contained' : 'outlined'}
          color="error"
          startIcon={<ThumbDownIcon />}
          disabled={submitting !== null}
          onClick={() => onSubmitVerdict('down', note)}
        >
          Bad draft
        </Button>
        <VerdictChip verdict={record.verdict} />
      </Stack>
      {record.verdictBy && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Last verdict by {record.verdictBy} on{' '}
          {record.verdictAt ? new Date(record.verdictAt).toLocaleString() : '—'}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Activity panel (design doc §6.7 panel 3) — a DataGrid over
 * `supportAgentTickets`, ONE ROW PER TICKET (pom-2-api 8/12 rework). Row
 * click opens a drawer showing the full Zendesk conversation plus a
 * newest-first "Bot activity" timeline of every append-only record for that
 * ticket (fetched via `getActivityRecords`) — verdict buttons on each
 * draft-bearing card POST and update that card's record in place.
 */
export const ActivityPanel: React.FC = () => {
  const [items, setItems] = useState<ActivityListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // DataGrid v5 is 0-indexed; API is 1-indexed
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await supportAgentApi.listActivity({ page: page + 1, limit: pageSize });
      setItems(res.items);
      setTotal(res.total);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [page, pageSize]);

  // ── detail drawer ───────────────────────────────────────────────────
  const [selectedRow, setSelectedRow] = useState<ActivityListItem | null>(null);
  const [docTitles, setDocTitles] = useState<Record<string, string>>({});

  // Full Zendesk conversation, shown in the "Conversation" section.
  // Deliberately independent of the records/verdict state below — a thread
  // fetch failure must not hide the timeline, and vice versa.
  const [thread, setThread] = useState<ActivityThreadResult | null>(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState<string | null>(null);

  // "Bot activity" timeline — ALL records for the ticket, newest first.
  const [records, setRecords] = useState<SupportAgentTicketRecord[] | null>(null);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsError, setRecordsError] = useState<string | null>(null);
  const [verdictError, setVerdictError] = useState<string | null>(null);
  const [submittingVerdict, setSubmittingVerdict] = useState<{ recordId: string; verdict: 'up' | 'down' } | null>(
    null,
  );

  // ── Per-ticket posting toggle (Carlos, 8/17) ──────────────────────────
  const [postingConfirmOpen, setPostingConfirmOpen] = useState(false);
  const [postingSubmitting, setPostingSubmitting] = useState(false);
  const [postingError, setPostingError] = useState<string | null>(null);
  const [postingSnackbar, setPostingSnackbar] = useState<string | null>(null);

  // Request-sequence guard: two quick row clicks fire two overlapping sets
  // of GET requests, and network timing does not guarantee they resolve in
  // click order. Without this, an unconditional `setRecords`/`setThread` in
  // a stale `.then` could apply row A's response AFTER row B's click has
  // already updated `selectedRow`. Bumped on every openDrawer/closeDrawer
  // call; a resolving request only applies its result if it's still the
  // most recent one issued.
  const requestSeqRef = useRef(0);

  const openDrawer = (row: ActivityListItem) => {
    const seq = ++requestSeqRef.current;
    setSelectedRow(row);
    setVerdictError(null);
    setRecords(null);
    setRecordsError(null);
    setRecordsLoading(true);
    setThread(null);
    setThreadError(null);
    setThreadLoading(true);

    // Two independent, concurrent fetches sharing one race-guard token —
    // both are keyed on the same row, so a later row click (which bumps
    // `requestSeqRef`) invalidates whichever of the two is still in flight
    // regardless of which resolves first.
    (async () => {
      try {
        const result = await supportAgentApi.getActivityRecords(row.zendeskTicketId);
        if (seq !== requestSeqRef.current) return; // a newer row was opened (or the drawer closed) while this was in flight
        setRecords(result);
      } catch (err: any) {
        if (seq !== requestSeqRef.current) return;
        setRecordsError(err?.response?.data?.message || err.message);
      } finally {
        if (seq === requestSeqRef.current) setRecordsLoading(false);
      }
    })();

    (async () => {
      try {
        const result = await supportAgentApi.getActivityThread(row.zendeskTicketId);
        if (seq !== requestSeqRef.current) return;
        setThread(result);
      } catch (err) {
        if (seq !== requestSeqRef.current) return;
        setThreadError(THREAD_UNAVAILABLE_COPY);
      } finally {
        if (seq === requestSeqRef.current) setThreadLoading(false);
      }
    })();
  };

  const closeDrawer = () => {
    requestSeqRef.current += 1; // invalidate any in-flight records/thread fetch
    setSelectedRow(null);
    setRecords(null);
    setRecordsError(null);
    setThread(null);
    setThreadError(null);
    setPostingConfirmOpen(false);
    setPostingError(null);
  };

  // Resolve cited KB doc titles for display via the bulk lookup (one
  // request per drawer open, across every record's citations, not one per
  // citation — GET /kb/:id pulls the full body + version history per doc,
  // which was an N+1 that scaled with citation count). Best-effort: a
  // cited doc may since have been archived/deleted, so ids missing from the
  // response fall back to the raw id.
  useEffect(() => {
    const ids = Array.from(new Set((records ?? []).flatMap((r) => r.citedDocIds ?? [])));
    const missing = ids.filter((id) => !(id in docTitles));
    if (missing.length === 0) return;
    (async () => {
      try {
        const results = await supportAgentApi.getKbDocTitles(missing);
        setDocTitles((prev) => ({
          ...prev,
          ...Object.fromEntries(results.map((r) => [r.id, r.title])),
        }));
      } catch (err) {
        // best-effort — leave the raw ids as the fallback display value
        console.warn('[ActivityPanel] getKbDocTitles failed — falling back to raw ids', err);
      }
    })();
  }, [records]);

  const submitVerdict = async (recordId: string, verdict: 'up' | 'down', note: string) => {
    if (!selectedRow) return;
    setSubmittingVerdict({ recordId, verdict });
    setVerdictError(null);
    try {
      const updated = await supportAgentApi.submitVerdict(selectedRow.zendeskTicketId, {
        verdict,
        note: note || undefined,
        recordId,
      });
      setRecords((prev) =>
        prev
          ? prev.map((r) =>
              r.recordId === recordId
                ? { ...r, verdict: updated.verdict, verdictBy: updated.verdictBy, verdictAt: updated.verdictAt, verdictNote: updated.verdictNote }
                : r,
            )
          : prev,
      );
      // The grid's row-level `verdict` field mirrors the LATEST record's
      // verdict — only touch it (and the open drawer's header row) when the
      // card just verdicted IS that latest record.
      if (recordId === selectedRow.recordId) {
        setSelectedRow((prev) => (prev ? { ...prev, verdict: updated.verdict } : prev));
        setItems((prev) =>
          prev.map((it) => (it.zendeskTicketId === selectedRow.zendeskTicketId ? { ...it, verdict: updated.verdict } : it)),
        );
      }
    } catch (err: any) {
      setVerdictError(err?.response?.data?.message || err.message);
    } finally {
      setSubmittingVerdict(null);
    }
  };

  // Applies a posting-enrollment change to both the grid row (`items`) and
  // the open drawer's header (`selectedRow`) in lockstep — the two are
  // separate pieces of state (see `openDrawer` above) but must always agree
  // on this ticket's enrollment.
  const setPostingEnabledLocally = (zendeskTicketId: number, enabled: boolean) => {
    setItems((prev) => prev.map((it) => (it.zendeskTicketId === zendeskTicketId ? { ...it, postingEnabled: enabled } : it)));
    setSelectedRow((prev) => (prev && prev.zendeskTicketId === zendeskTicketId ? { ...prev, postingEnabled: enabled } : prev));
  };

  // Disabling is not destructive (existing notes stay, only future
  // auto-posts stop) — no confirm dialog, unlike enabling below. Optimistic:
  // flips the switch immediately, reverts on API failure.
  const disablePosting = async () => {
    if (!selectedRow) return;
    const { zendeskTicketId } = selectedRow;
    const previous = !!selectedRow.postingEnabled;
    setPostingSubmitting(true);
    setPostingError(null);
    setPostingEnabledLocally(zendeskTicketId, false);
    try {
      const result = await supportAgentApi.updatePosting(zendeskTicketId, false);
      setPostingEnabledLocally(zendeskTicketId, result.enabled);
    } catch (err: any) {
      setPostingEnabledLocally(zendeskTicketId, previous);
      setPostingError(err?.response?.data?.message || err.message);
    } finally {
      setPostingSubmitting(false);
    }
  };

  // Switch's onChange — enabling always routes through the confirm dialog
  // (it posts the current draft immediately, as an internal note, and
  // enrolls every future draft on this ticket); disabling is immediate.
  const handlePostingSwitchChange = (checked: boolean) => {
    if (checked) {
      setPostingConfirmOpen(true);
    } else {
      disablePosting();
    }
  };

  const confirmEnablePosting = async () => {
    if (!selectedRow) return;
    const { zendeskTicketId } = selectedRow;
    const previous = !!selectedRow.postingEnabled;
    setPostingSubmitting(true);
    setPostingError(null);
    setPostingEnabledLocally(zendeskTicketId, true);
    try {
      const result = await supportAgentApi.updatePosting(zendeskTicketId, true);
      setPostingEnabledLocally(zendeskTicketId, result.enabled);
      setPostingConfirmOpen(false);
      setPostingSnackbar(immediatePostMessage(result.immediatePost));
    } catch (err: any) {
      setPostingEnabledLocally(zendeskTicketId, previous);
      setPostingError(err?.response?.data?.message || err.message);
    } finally {
      setPostingSubmitting(false);
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'zendeskTicketId',
        headerName: 'Ticket',
        width: 120,
        renderCell: (params) => (
          <MuiLink
            href={zendeskTicketUrl(params.value)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            #{params.value} <OpenInNewIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} />
          </MuiLink>
        ),
      },
      {
        field: 'subject',
        headerName: 'Subject',
        flex: 1.2,
        valueGetter: (p) => p.row.subject ?? '—',
      },
      { field: 'topic', headerName: 'Topic', flex: 1, valueGetter: (p) => p.row.topic ?? '—' },
      {
        field: 'urgent',
        headerName: 'Urgency',
        width: 110,
        renderCell: (params) => (params.row.urgent ? <Chip size="small" color="error" label="Urgent" /> : null),
      },
      {
        field: 'mode',
        headerName: 'Mode',
        width: 130,
        // A skipped row has no meaningful mode (drafting never ran) — show
        // the Skipped chip in this same slot instead of the Mode chip.
        renderCell: (params) =>
          (params.row as ActivityListItem).skipped ? (
            <SkippedChip category={(params.row as ActivityListItem).category} />
          ) : (
            <ModeChip mode={resolveMode(params.row as ActivityListItem)} />
          ),
      },
      {
        field: 'postingEnabled',
        headerName: 'Posting',
        width: 110,
        renderCell: (params) => ((params.row as ActivityListItem).postingEnabled ? <PostingChip /> : null),
      },
      {
        field: 'eventCount',
        headerName: 'Events',
        width: 100,
        valueGetter: (p) => p.row.eventCount ?? 1,
      },
      {
        field: 'lastActivityAt',
        headerName: 'Last activity',
        width: 200,
        valueGetter: (p) => new Date(p.row.lastActivityAt ?? p.row.createdAt).toLocaleString(),
      },
    ],
    [],
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={items}
          // zendeskTicketId, NOT recordId — the list is now one row per
          // ticket (pom-2-api 8/12 rework); the append-only per-record
          // history lives in the drawer's "Bot activity" timeline instead.
          getRowId={(row) => row.zendeskTicketId}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={total}
          page={page}
          pageSize={pageSize}
          onPageChange={(p) => setPage(p)}
          onPageSizeChange={(s) => setPageSize(s)}
          rowsPerPageOptions={[10, 25, 50, 100]}
          disableSelectionOnClick
          onRowClick={(params) => openDrawer(params.row as ActivityListItem)}
        />
      </Box>

      <Drawer anchor="right" open={selectedRow !== null} onClose={closeDrawer} PaperProps={{ sx: { width: 560 } }}>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6">
              {selectedRow != null && (
                <MuiLink href={zendeskTicketUrl(selectedRow.zendeskTicketId)} target="_blank" rel="noopener noreferrer">
                  Ticket #{selectedRow.zendeskTicketId}
                </MuiLink>
              )}
            </Typography>
            <IconButton onClick={closeDrawer} size="small" aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          {selectedRow && (
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!selectedRow.postingEnabled}
                      disabled={postingSubmitting}
                      onChange={(_, checked) => handlePostingSwitchChange(checked)}
                      inputProps={{ 'aria-label': 'Post drafts to Zendesk (this ticket)' }}
                    />
                  }
                  label="Post drafts to Zendesk (this ticket)"
                />
                {selectedRow.postingEnabled && <PostingChip />}
              </Stack>
              {/* Suppressed while the confirm dialog is open — the dialog shows this same error there, and
                  MUI portals the dialog into the DOM alongside this box, so showing both would duplicate the
                  text node for any test/query keyed on the error message. */}
              {postingError && !postingConfirmOpen && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setPostingError(null)}>
                  {postingError}
                </Alert>
              )}
            </Box>
          )}

          {selectedRow && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Conversation
              </Typography>
              {threadLoading && <CircularProgress size={20} />}
              {threadError && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  {threadError}
                </Alert>
              )}
              {thread && !threadLoading && (
                <>
                  {thread.truncated && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Showing the 100 most recent comments
                    </Typography>
                  )}
                  <Box
                    sx={{
                      maxHeight: 400,
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      pr: 0.5,
                    }}
                  >
                    {classifyThreadComments(thread.comments).map((c) => (
                      <ThreadCommentBubble key={c.id} comment={c} />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}

          {selectedRow && (
            <Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Bot activity
              </Typography>
              {recordsLoading && <CircularProgress size={20} />}
              {recordsError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {recordsError}
                </Alert>
              )}
              {verdictError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {verdictError}
                </Alert>
              )}
              {records && !recordsLoading && (
                <Stack spacing={1.5}>
                  {records.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No activity recorded for this ticket.
                    </Typography>
                  )}
                  {records.map((record) => {
                    if (record.skipped) {
                      // Skipped: the pre-draft triage classified this
                      // message as sales/vendor/spam and drafting never
                      // ran — no draft/citations/verdict worth a full card,
                      // same treatment as an observe-only record.
                      return (
                        <Box
                          key={record.recordId}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {new Date(record.createdAt).toLocaleString()}
                          </Typography>
                          <SkippedChip category={record.category} />
                          {record.skipReason && (
                            <Typography variant="body2" color="text.secondary">
                              {record.skipReason}
                            </Typography>
                          )}
                        </Box>
                      );
                    }
                    const mode = resolveMode(record);
                    if (mode === 'observe') {
                      // Observe-only: the bot watched but never drafted, so
                      // there's no draft/citations/verdict worth a full
                      // card for — a compact line is enough context.
                      return (
                        <Typography key={record.recordId} variant="body2" color="text.secondary">
                          {new Date(record.createdAt).toLocaleString()} — Message observed — no draft (observe mode)
                        </Typography>
                      );
                    }
                    return (
                      <TimelineRecordCard
                        key={record.recordId}
                        record={record}
                        docTitles={docTitles}
                        submitting={submittingVerdict?.recordId === record.recordId ? submittingVerdict.verdict : null}
                        onSubmitVerdict={(verdict, note) => submitVerdict(record.recordId, verdict, note)}
                      />
                    );
                  })}
                </Stack>
              )}
            </Box>
          )}
        </Box>
      </Drawer>

      <Dialog open={postingConfirmOpen} onClose={() => (postingSubmitting ? undefined : setPostingConfirmOpen(false))}>
        <DialogTitle>Post drafts to Zendesk for this ticket?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            The current draft (if any) will be posted immediately as an <strong>internal note</strong> — never
            visible to the customer. Every future draft on this ticket will also post automatically.
          </DialogContentText>
          <DialogContentText>Disabling later stops future posts — notes already posted stay.</DialogContentText>
          {postingError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {postingError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostingConfirmOpen(false)} disabled={postingSubmitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmEnablePosting} disabled={postingSubmitting}>
            {postingSubmitting ? 'Enabling…' : 'Enable'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={postingSnackbar !== null}
        autoHideDuration={5000}
        onClose={() => setPostingSnackbar(null)}
        message={postingSnackbar}
      />
    </Box>
  );
};
